import { isAbsoluteUrl, isCentralUrl, isDataUrl } from '@common/util/path';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { ContentNodeViewOptions } from '@portal-core/text-editor/node-views/models/content-node-view-options.model';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { LoadingState } from '@portal-core/util/loading-state';
import { Node } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';
import { Observable } from 'rxjs';

export class ImageNodeView extends NodeViewBase<HTMLImageElement> implements NodeView {
  private loadingState: LoadingState<string> = new LoadingState<string>();

  constructor(
    node: Node,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>,
    private options?: ContentNodeViewOptions
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Sync the node
    this.syncNode(node);
  }

  /** Override getNodeAttrs to omit the src attribute from being applied to the dom element. It will be handled in this node view. */
  getNodeAttrs(attrs: Dictionary<string>): Dictionary<string> {
    attrs = { ...attrs };
    delete attrs.src;
    return attrs;
  }

  syncNode(node: Node) {
    // Determine what the src should be for the image
    let src: string;

    if (isDataUrl(node.attrs.src)) {
      src = node.attrs.src;
    } else if (isCentralUrl(node.attrs.src)) {
      if (typeof this.options?.resolveCentralUrl === 'function') {
        src = this.options.resolveCentralUrl(node.attrs.src);
      } else {
        throw new Error('resolveCentralUrl is not defined in ContentNodeViewOptions');
      }
    } else if (isAbsoluteUrl(node.attrs.src)) {
      src = node.attrs.src;
    } else if (node.attrs.src && this.options && typeof this.options.buildContentApiUri === 'function') {
      src = this.options.buildContentApiUri(node.attrs.src);
    } else {
      src = null;
    }

    // Update the img element and load the image
    if (src !== this.dom.src) {
      if (src) {
        this.dom.src = ''; // There must be a string src for the element to render at all including its psuedo elements for loading/error states
        this.dom.setAttribute('mc-lwe-image-text', 'Loading...');
        this.loadingState.update(true);

        this.loadImage$(src).subscribe(() => {
          this.dom.src = src;
          this.dom.removeAttribute('mc-lwe-image-text');
          this.loadingState.update(false);
          this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
        }, () => {
          this.dom.setAttribute('mc-lwe-image-text', 'Unable to load image');
          this.loadingState.update(false, 'Unable to load image');
          this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
        });
      } else {
        this.dom.src = ''; // There must be a string src for the element to render at all including its psuedo elements for loading/error states
        this.dom.setAttribute('mc-lwe-image-text', 'Empty image');
        this.loadingState.update(false);
        this.emitEvent('node-view-size-changed', { nodeName: node.type.name });
      }
    }
  }

  private loadImage$(path: string): Observable<HTMLImageElement> {
    return new Observable(observer => {
      try {
        const img = new Image();

        img.onload = function () {
          observer.next(img);
          observer.complete();
        };

        img.onerror = function (error: Event | string) {
          observer.error(error);
          observer.complete();
        };

        img.src = path;
      } catch (ex) {
        observer.error(ex);
        observer.complete();
      }
    });
  }
}
